import { Translation } from '../../../../translation/types';
import { IndicatorStatus } from '../../components/sportsbook-indicator';
import BetslipTextKeys from '~/components/sportsbook/lobby/features/betslip/sportsbook-betslip-text-keys';
import { isBetNeedToFeedbackAnError, isUnavailableError, sortErrorsByPriority } from './sportsbook-betslip-error-utils';
import {
	BetBonus,
	BetBonusType,
	BetPlacementStatus,
	BetslipBet,
	BetslipBetError,
	BetslipBetItemWithData,
	BetslipBetWithData,
	BetslipBetWithCompleteData,
	BetslipBetItemWithCompleteData,
	BetslipPersistedBet,
	BetslipBetWarning
} from './sportsbook-betslip-types';
import BetTextKeys from '~/components/sportsbook/lobby/components/bet/sportsbook-bet-text-keys';

export const betPlacementStatusToIndicatorStatus = (betPlacementStatus: BetPlacementStatus) => {
	switch (betPlacementStatus) {
		case BetPlacementStatus.DECLINED:
		case BetPlacementStatus.NEW_OFFER_REJECTED:
		case BetPlacementStatus.UNKNOWN:
			return IndicatorStatus.FAILURE;
		case BetPlacementStatus.PLACED:
			return IndicatorStatus.SUCCESS;
		case BetPlacementStatus.NEW_OFFER_PENDING:
			return IndicatorStatus.INFO;
		case BetPlacementStatus.EDIT:
		case BetPlacementStatus.PLACING:
		default:
			return IndicatorStatus.NEUTRAL;
	}
};

export const betToPersistedBet = (bet: BetslipBetWithData): BetslipPersistedBet => ({
	stake: bet.stake,
	type: bet.type,
	combination: bet.combination,
	items: bet.items.map(item => ({
		ref: item.ref,
		eventId: item.eventId,
		marketId: item.marketId,
		selectionId: item.selectionId,
		preferredInMarketType: item.preferredInMarketType,
		tag: item.tag
	}))
});

export const isBetAvailable = (bet: BetslipBetWithData) => !bet.errors.some(isUnavailableError);

export const hasFixedBonusStake = (bonus?: BetBonus) =>
	[BetBonusType.FREE_BET].includes(bonus?.bonusType as BetBonusType);

export const betCombinationToTextKeyMap: { [x: number]: string } = {
	1: BetTextKeys.SYSTEM_BET_COMBINATION_1_LABEL,
	2: BetTextKeys.SYSTEM_BET_COMBINATION_2_LABEL,
	3: BetTextKeys.SYSTEM_BET_COMBINATION_3_LABEL,
	4: BetTextKeys.SYSTEM_BET_COMBINATION_4_LABEL,
	5: BetTextKeys.SYSTEM_BET_COMBINATION_5_LABEL,
	6: BetTextKeys.SYSTEM_BET_COMBINATION_6_LABEL,
	7: BetTextKeys.SYSTEM_BET_COMBINATION_7_LABEL,
	8: BetTextKeys.SYSTEM_BET_COMBINATION_8_LABEL,
	9: BetTextKeys.SYSTEM_BET_COMBINATION_9_LABEL,
	10: BetTextKeys.SYSTEM_BET_COMBINATION_10_LABEL,
	11: BetTextKeys.SYSTEM_BET_COMBINATION_11_LABEL,
	12: BetTextKeys.SYSTEM_BET_COMBINATION_12_LABEL,
	13: BetTextKeys.SYSTEM_BET_COMBINATION_13_LABEL,
	14: BetTextKeys.SYSTEM_BET_COMBINATION_14_LABEL,
	15: BetTextKeys.SYSTEM_BET_COMBINATION_15_LABEL,
	16: BetTextKeys.SYSTEM_BET_COMBINATION_16_LABEL,
	17: BetTextKeys.SYSTEM_BET_COMBINATION_17_LABEL,
	18: BetTextKeys.SYSTEM_BET_COMBINATION_18_LABEL,
	19: BetTextKeys.SYSTEM_BET_COMBINATION_19_LABEL,
	20: BetTextKeys.SYSTEM_BET_COMBINATION_20_LABEL
};

export const isBetItemHasNewOfferOdds = (bet: BetslipBetWithData, item: BetslipBetItemWithData) =>
	bet.newOffer?.items.some(newOfferItem => item.selectionId === newOfferItem.selectionId) || false;

export const isBetHasSettledPlacementStatusOrNewOffer = (bet: BetslipBetWithData) =>
	[
		BetPlacementStatus.PLACED, // settled
		BetPlacementStatus.DECLINED, // settled
		BetPlacementStatus.NEW_OFFER_PENDING, // new offer
		BetPlacementStatus.NEW_OFFER_REJECTED // settled
	].includes(bet.placementStatus);

export const getBetItemIndicatorStatus = (
	hasItemError: boolean,
	betIndicatorStatus: IndicatorStatus,
	isNewOfferPending: boolean,
	isNewOfferOdds: boolean
): IndicatorStatus => {
	if (isNewOfferPending && !isNewOfferOdds) {
		return IndicatorStatus.NEUTRAL;
	}

	if (hasItemError) {
		return IndicatorStatus.FAILURE;
	}

	if (betIndicatorStatus === IndicatorStatus.FAILURE) {
		return IndicatorStatus.NEUTRAL;
	}

	return betIndicatorStatus;
};

export const isBetNewOfferPending = (bet: BetslipBet) => bet.placementStatus === BetPlacementStatus.NEW_OFFER_PENDING;

export const isBetNewOfferRejected = (bet: BetslipBet) => bet.placementStatus === BetPlacementStatus.NEW_OFFER_REJECTED;

export const isBetUnknown = (bet: BetslipBet) => bet.placementStatus === BetPlacementStatus.UNKNOWN;

export const shouldBetShowBonuses = (bet: BetslipBetWithData) =>
	isBetAvailable(bet) && (!!bet.selectedBonus || bet.bonuses.length > 0);

export const hasIncompleteData = (item: BetslipBetItemWithData) =>
	!item.isLoading && (!item.market || !item.event || !item.selection);

export const getBetSelectedBonus = (bet: BetslipBet) => bet.selectedBonus;

export const getBetError = (bet: BetslipBet, isPlacementOrReceipt?: boolean) => {
	if (isPlacementOrReceipt) {
		return bet.errors[0];
	}
	// For edit
	const errors = sortErrorsByPriority(bet.errors.filter(err => isBetNeedToFeedbackAnError(err)));
	return errors[0] as BetslipBetError | undefined;
};

export const getBetWarning = (bet: BetslipBet, betError: BetslipBetError | undefined) => {
	if (betError) {
		return undefined;
	}
	return bet.warnings[0] as BetslipBetWarning | undefined;
};

export const getBonusStakeLabel = (bonus?: BetBonus, translations?: Translation) => {
	if (!translations) {
		return undefined;
	}
	if (bonus?.bonusType === BetBonusType.FREE_BET) {
		return translations[BetslipTextKeys.FREE_BET_STAKE_TITLE];
	}
	if (bonus?.bonusType === BetBonusType.RISK_FREE_BET) {
		return translations[BetslipTextKeys.RISK_FREE_BET_STAKE_TITLE];
	}
	return undefined;
};

export const getBetPlacementIndicatorStatus = (bet: BetslipBet) =>
	betPlacementStatusToIndicatorStatus(bet.placementStatus);

export const isBetBoostBonus = (bonus?: BetBonus) => bonus?.bonusType === BetBonusType.BET_BOOST;

export const isSuperBoostBonus = (bonus?: BetBonus) => bonus?.bonusType === BetBonusType.SUPER_BOOST;

export const isBetWithCompleteData = (bet: BetslipBetWithData): bet is BetslipBetWithCompleteData =>
	bet.items.every(
		(item): item is BetslipBetItemWithCompleteData => !!item.event && !!item.market && !!item.selection
	);
export const areBetItemsWithNewOfferPending = (bets: BetslipBetWithData[], selectionId: string) => {
	const uniqueIds = new Set(bets.flatMap(bet => bet.newOffer?.items.map(i => i.selectionId).filter(Boolean)));
	return uniqueIds.has(selectionId);
};

export const areBetItemsWithNewOfferRejected = (bet: BetslipBetWithData, selectionId: string) => {
	const uniqueIds = new Set(bet.newOffer?.items.map(i => i.selectionId).filter(Boolean));
	return uniqueIds.has(selectionId);
};

export const lastBetCreatedDate = (bets: BetslipBetWithData[]) => {
	const dates = bets
		.map(bet => bet.createdDate && new Date(bet.createdDate))
		.filter(date => date && true && date?.getTime()) as Date[];

	return dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))).toString() : '';
};

// TODO Remove all usages of this function and instead use bet.odds once bet.originalOdds is removed from api
export const getBetOdds = (bet: BetslipBetWithData) => {
	if (isSuperBoostBonus(bet.selectedBonus)) {
		return bet.originalOdds;
	}
	// this is true for both no bonusand isBetBoostBonus
	return bet.odds;
};

export const isBoostedBet = (bet: BetslipBetWithData) => {
	if (isSuperBoostBonus(bet.selectedBonus) || isBetBoostBonus(bet.selectedBonus)) {
		return true;
	}
	return false;
};
